import { FeatureFlags } from './featureFlags'
import { RoleType } from '../utils/jwtUtils'

type FeatureFlag = (typeof FeatureFlags)[keyof typeof FeatureFlags]

/** Who a help section is written for. Every account is a player; managers and admins get their own sections on top. */
export enum HelpAudience {
  PLAYER = 'player',
  MANAGER = 'manager',
  ADMIN = 'admin',
}

/** The roles that can open the pages an audience's sections describe (the same split as the route guards). */
export const HELP_AUDIENCE_ROLES: Record<HelpAudience, RoleType[] | null> = {
  [HelpAudience.PLAYER]: null,
  [HelpAudience.MANAGER]: [RoleType.MANAGER, RoleType.ADMIN],
  [HelpAudience.ADMIN]: [RoleType.ADMIN],
}

export type HelpTopic = {
  id: string
  /** The page the topic is about, opened by the topic's "Go to page" button. */
  path?: string
  /** Where the page sits in the menus, as translation keys (namespace included), outermost first. */
  crumbs?: readonly string[]
  flag?: FeatureFlag
}

export type HelpSection = {
  id: string
  audience: HelpAudience
  icon: string
  flag?: FeatureFlag
  topics: readonly HelpTopic[]
}

const USER_MENU = 'help:crumbs.user-menu'
const OPERATIONS = 'nav:side-bar.section.operations'
const SETUP = 'nav:side-bar.section.setup'
const ADMINISTRATION = 'nav:side-bar.section.administration'

const account = (section: string, tab: string, path: string): HelpTopic => ({
  id: tab,
  path,
  crumbs: [USER_MENU, 'nav:user-menu.profile', `account:account.sections.${section}`, `account:account.tabs.${tab}`],
})

/**
 * Everything the app lets a signed-in user do, grouped by the menu it lives in. The texts are in the `help` namespace
 * under `sections.<id>` and `topics.<id>`; keep this list in step with the routes in `App.tsx` and the menus in
 * `navigation.ts`.
 */
export const HELP_SECTIONS: readonly HelpSection[] = [
  {
    id: 'getting-started',
    audience: HelpAudience.PLAYER,
    icon: 'sparkle',
    topics: [
      { id: 'sign-in' },
      { id: 'account-setup', path: '/account/setup' },
      { id: 'confirm-email', path: '/booking', crumbs: ['nav:side-bar.book-court'] },
      { id: 'find-your-way' },
      { id: 'sign-out', crumbs: [USER_MENU, 'nav:top-bar.sign-out'] },
    ],
  },
  {
    id: 'dashboard',
    audience: HelpAudience.PLAYER,
    icon: 'home',
    topics: [
      { id: 'dashboard-overview', path: '/dashboard', crumbs: ['nav:side-bar.dashboard'] },
      { id: 'upcoming-bookings', path: '/dashboard', crumbs: ['nav:side-bar.dashboard'] },
      { id: 'gate-access', path: '/dashboard', crumbs: ['nav:side-bar.dashboard'], flag: FeatureFlags.FF_GATE },
      { id: 'locker-access', path: '/dashboard', crumbs: ['nav:side-bar.dashboard'], flag: FeatureFlags.FF_LOCKER },
    ],
  },
  {
    id: 'booking',
    audience: HelpAudience.PLAYER,
    icon: 'calendar',
    topics: [
      { id: 'book-court', path: '/booking', crumbs: ['nav:side-bar.book-court'] },
      { id: 'slot-legend', path: '/booking', crumbs: ['nav:side-bar.book-court'] },
      {
        id: 'book-with-passes',
        path: '/booking',
        crumbs: ['nav:side-bar.book-court'],
        flag: FeatureFlags.FF_MEMBERSHIPS,
      },
      { id: 'use-refund-points', path: '/booking', crumbs: ['nav:side-bar.book-court'] },
      { id: 'checkout', path: '/booking', crumbs: ['nav:side-bar.book-court', 'nav:top-bar.checkout'] },
      { id: 'finish-payment', path: '/history', crumbs: ['nav:side-bar.my-bookings'] },
    ],
  },
  {
    id: 'my-bookings',
    audience: HelpAudience.PLAYER,
    icon: 'history',
    topics: [
      { id: 'view-bookings', path: '/history', crumbs: ['nav:side-bar.my-bookings'] },
      { id: 'reschedule-booking', path: '/history', crumbs: ['nav:side-bar.my-bookings'] },
      { id: 'cancel-booking', path: '/history', crumbs: ['nav:side-bar.my-bookings'] },
    ],
  },
  {
    id: 'passes',
    audience: HelpAudience.PLAYER,
    icon: 'crown',
    flag: FeatureFlags.FF_MEMBERSHIPS,
    topics: [
      { id: 'buy-pass', path: '/passes', crumbs: ['nav:side-bar.passes'] },
      { id: 'pass-rules', path: '/passes', crumbs: ['nav:side-bar.passes'] },
      { id: 'my-passes', path: '/passes', crumbs: ['nav:side-bar.passes'] },
    ],
  },
  {
    id: 'partners',
    audience: HelpAudience.PLAYER,
    icon: 'users',
    flag: FeatureFlags.FF_PARTNERS,
    topics: [
      { id: 'invite-partner', path: '/partners', crumbs: ['nav:side-bar.partners'] },
      { id: 'partner-requests', path: '/partners', crumbs: ['nav:side-bar.partners'] },
      { id: 'manage-partners', path: '/partners', crumbs: ['nav:side-bar.partners'] },
      { id: 'book-together', path: '/partners', crumbs: ['nav:side-bar.partners'] },
    ],
  },
  {
    id: 'account',
    audience: HelpAudience.PLAYER,
    icon: 'user',
    topics: [
      account('profile', 'profile', '/account/profile'),
      account('billing', 'payments', '/account/payments'),
      account('billing', 'refunds', '/account/refunds'),
      account('billing', 'billing-address', '/account/billing'),
      account('settings', 'preferences', '/account/preferences'),
      account('settings', 'notifications', '/account/notifications'),
      account('security', 'password', '/account/password'),
      account('security', 'terms', '/account/terms'),
      account('security', 'delete-account', '/account/delete-account'),
    ],
  },
  {
    id: 'schedule',
    audience: HelpAudience.MANAGER,
    icon: 'grid',
    topics: [
      { id: 'daily-schedule', path: '/m/all', crumbs: [OPERATIONS, 'nav:side-bar.schedule'] },
      { id: 'cancel-customer-booking', path: '/m/all', crumbs: [OPERATIONS, 'nav:side-bar.schedule'] },
    ],
  },
  {
    id: 'customer-bookings',
    audience: HelpAudience.MANAGER,
    icon: 'plus',
    topics: [
      { id: 'book-for-customer', path: '/m/booking', crumbs: [OPERATIONS, 'nav:side-bar.new-booking'] },
      { id: 'new-customer', path: '/m/booking', crumbs: [OPERATIONS, 'nav:side-bar.new-booking'] },
      { id: 'customer-funding', path: '/m/booking', crumbs: [OPERATIONS, 'nav:side-bar.new-booking'] },
    ],
  },
  {
    id: 'court-blocks',
    audience: HelpAudience.MANAGER,
    icon: 'block',
    topics: [
      { id: 'create-block', path: '/m/blocks/new', crumbs: [OPERATIONS, 'nav:side-bar.court-blocks'] },
      { id: 'block-conflicts', path: '/m/blocks', crumbs: [OPERATIONS, 'nav:side-bar.court-blocks'] },
      { id: 'manage-blocks', path: '/m/blocks', crumbs: [OPERATIONS, 'nav:side-bar.court-blocks'] },
    ],
  },
  {
    id: 'payments',
    audience: HelpAudience.MANAGER,
    icon: 'card',
    topics: [
      { id: 'review-payments', path: '/m/payments', crumbs: [OPERATIONS, 'nav:side-bar.payments'] },
      { id: 'settle-payment', path: '/m/payments', crumbs: [OPERATIONS, 'nav:side-bar.payments'] },
    ],
  },
  {
    id: 'clubs',
    audience: HelpAudience.MANAGER,
    icon: 'flag',
    topics: [
      { id: 'create-club', path: '/m/clubs/new', crumbs: [SETUP, 'nav:side-bar.clubs'] },
      { id: 'club-rules', path: '/m/clubs', crumbs: [SETUP, 'nav:side-bar.clubs'] },
      { id: 'manage-clubs', path: '/m/clubs', crumbs: [SETUP, 'nav:side-bar.clubs'] },
    ],
  },
  {
    id: 'courts',
    audience: HelpAudience.MANAGER,
    icon: 'indoor',
    topics: [
      { id: 'create-court', path: '/m/courts/new', crumbs: [SETUP, 'nav:side-bar.courts'] },
      { id: 'manage-courts', path: '/m/courts', crumbs: [SETUP, 'nav:side-bar.courts'] },
      { id: 'night-lights', path: '/m/courts', crumbs: [SETUP, 'nav:side-bar.courts'] },
      { id: 'remove-court', path: '/m/courts', crumbs: [SETUP, 'nav:side-bar.courts'] },
    ],
  },
  {
    id: 'session-prices',
    audience: HelpAudience.MANAGER,
    icon: 'tag',
    topics: [
      { id: 'create-price-rule', path: '/m/pricing/new', crumbs: [SETUP, 'nav:side-bar.session-prices'] },
      { id: 'price-versions', path: '/m/pricing', crumbs: [SETUP, 'nav:side-bar.session-prices'] },
    ],
  },
  {
    id: 'pass-products',
    audience: HelpAudience.MANAGER,
    icon: 'crown',
    flag: FeatureFlags.FF_MEMBERSHIPS,
    topics: [
      { id: 'create-pass-product', path: '/m/passes/new', crumbs: [SETUP, 'nav:side-bar.passes'] },
      { id: 'pass-product-status', path: '/m/passes', crumbs: [SETUP, 'nav:side-bar.passes'] },
      { id: 'pass-product-versions', path: '/m/passes', crumbs: [SETUP, 'nav:side-bar.passes'] },
    ],
  },
  {
    id: 'reports',
    audience: HelpAudience.ADMIN,
    icon: 'chart',
    topics: [
      { id: 'read-reports', path: '/a/reports', crumbs: [ADMINISTRATION, 'nav:side-bar.reports'] },
      { id: 'report-filters', path: '/a/reports', crumbs: [ADMINISTRATION, 'nav:side-bar.reports'] },
    ],
  },
  {
    id: 'users',
    audience: HelpAudience.ADMIN,
    icon: 'users',
    topics: [
      { id: 'find-users', path: '/a/users', crumbs: [ADMINISTRATION, 'nav:side-bar.users'] },
      { id: 'change-roles', path: '/a/users', crumbs: [ADMINISTRATION, 'nav:side-bar.users'] },
      { id: 'lock-user', path: '/a/users', crumbs: [ADMINISTRATION, 'nav:side-bar.users'] },
      { id: 'reset-delete-user', path: '/a/users', crumbs: [ADMINISTRATION, 'nav:side-bar.users'] },
    ],
  },
  {
    id: 'audit',
    audience: HelpAudience.ADMIN,
    icon: 'lock',
    topics: [
      { id: 'account-actions', path: '/a/audit', crumbs: [ADMINISTRATION, 'nav:side-bar.audit-log'] },
      { id: 'sign-in-audit', path: '/a/audit?view=sign-ins', crumbs: [ADMINISTRATION, 'nav:side-bar.audit-log'] },
    ],
  },
]
